//@flow
import * as React from 'react';
import { DropzoneWithPopover } from 'common/components/core/dropzone';
import Icon from 'common/components/core/icon';
import Button, { ButtonTypes } from 'common/components/core/button';
import { SizeEnum } from 'common/enums/UIEnums';
import { clientSelector } from 'common/redux/selectors/queryParamsSelectors';
import { connect } from 'react-redux';
import { compose } from 'recompose';
import { withRouter } from 'common/components/core/router';
import { DocumentWithTooltip } from 'common/components/core/document/DocumentWithPreview';
import { entityFileUploadApi } from 'common/middleware/api';
import { Loader } from 'common/components/core/loader/Loader';
import { showToast } from 'common/redux/actions/toast';
import { ToastTypes } from 'common/components/core/toast/Toast';
import DataUtils from 'common/utils/DataUtils';

type Files = Array<File>;

type Props = {
    theme: Object,
    className: string,
    file: File,
    label: string,
    required: boolean,
    placeholder: string,
    dialogStatus: string,
    onChange: (file: File) => void,
    onRemoveFile: (fileIndex: number) => void,
    getDropzoneContainer: () => void,
    currentDocument: Object,
    clientId: string,
    showToast: (Object, Object) => void,
    maxSizeText: string,
    t: (string, ?Object) => string,
    onChangeHandler: Object => void,
    accept: Array<string>,
    maxSize: number
};

type State = {
    isLoading: boolean
};
class EntityDocumentUpload extends React.Component<Props, State> {
    state = {
        isLoading: false
    };

    onDrop = (files: Array<File>, rejectedFiles: Files) => {
        const [file] = files;
        const { clientId, maxSizeText, t, showToast } = this.props;
        if (rejectedFiles.length > 0) {
            showToast(
                {
                    message: t('documentUpload.fileUploadFailure'),
                    subText: t('documentUpload.fileUploadSizeError', {
                        maxSizeText
                    }),
                    type: ToastTypes.Error
                },
                { clientId }
            );
            return;
        }
        this.onChange(file);
    };
    onChange = file => {
        const {
            clientId,
            currentDocument,
            showToast,
            t,
            onChange,
            onChangeHandler
        } = this.props;
        const { id, type } = currentDocument;
        const params = { clientId };
        const formData = new FormData();
        formData.append('file', file);
        this.setState({
            isLoading: true
        });
        entityFileUploadApi
            .post(params, formData)
            .then(res => {
                this.setState({ isLoading: false });
                if (currentDocument.type === 'logo') {
                    onChange(res.url);
                } else {
                    let newId = id;
                    if (!newId) {
                        newId = `${type}_${Date.now()}`;
                    }
                    onChangeHandler(
                        currentDocument.set('id', newId).setImageUrl(res.url)
                    );
                }
            })
            .catch(() => {
                this.setState({ isLoading: false });
                showToast(
                    {
                        message: t('documentUpload.fileUploadFailure'),
                        subText: t('documentUpload.fileUploadError'),
                        type: ToastTypes.Error
                    },
                    { clientId }
                );
            });
    };
    getDropzoneContainer = ref => {
        this.dropzone = ref;
    };

    openFileDialog = (): void => {
        const { currentDocument, onChange, onChangeHandler } = this.props;
        if (!DataUtils.isEmpty(currentDocument.imageUrl)) {
            if (currentDocument.type === 'logo') {
                onChange('');
            } else {
                onChangeHandler(currentDocument.set('id', '').setImageUrl(''));
            }
        } else {
            this.dropzone && this.dropzone.open();
        }
    };
    renderFileIcon = filePreview => {
        const { theme } = this.props;
        const { isLoading } = this.state;
        if (isLoading) {
            return <Loader active theme={theme} />;
        }
        if (filePreview) {
            return (
                <DocumentWithTooltip
                    key={filePreview}
                    src={filePreview}
                    theme={theme}
                />
            );
        }
        return <Icon theme={theme} icon="file-upload" />;
    };

    render() {
        const { currentDocument, theme, t, accept, maxSize } = this.props;
        const { isLoading } = this.state;
        const { multiple, imageUrl } = currentDocument;
        const validationMessage =
            currentDocument.getValidationMessage &&
            currentDocument.getValidationMessage('imageUrl');
        return (
            <div className={theme.dropzoneWrap}>
                <DropzoneWithPopover
                    accept={accept}
                    maxSize={maxSize}
                    multiple={multiple}
                    theme={theme}
                    inlineDropzone
                    className={theme.dropzone}
                    activeClassName={theme.dropzoneActive}
                    rejectClassName={theme.dropzoneReject}
                    getDropzoneContainer={this.getDropzoneContainer}
                    onDrop={this.onDrop}
                    isPopOverOpen={!isLoading && validationMessage}
                    popOverContent={validationMessage}
                >
                    {this.renderFileIcon(imageUrl)}
                </DropzoneWithPopover>
                <Button
                    buttonType={ButtonTypes.Flat}
                    noPadding
                    ripple={false}
                    semibold
                    label={
                        imageUrl
                            ? t('documentUpload.remove')
                            : t('documentUpload.upload')
                    }
                    theme={theme}
                    className={theme.uploadButton}
                    onClick={this.openFileDialog}
                    size={SizeEnum.Small}
                    disabled={isLoading}
                />
            </div>
        );
    }
}

const mapStateToProps = (state: Object, ownProps: Object) => {
    const clientId = clientSelector(state, ownProps);

    return {
        clientId
    };
};

const mapDispatchToProps = {
    showToast
};

const EnhancedEntityDocumentUpload = compose(
    withRouter,
    connect(mapStateToProps, mapDispatchToProps)
)(EntityDocumentUpload);

export default EnhancedEntityDocumentUpload;
export { EnhancedEntityDocumentUpload as EntityDocumentUpload };
